import type { DbClient } from "@/server/platform/db/client";
import type { DbTransaction } from "@/shared/types";
import { getVariantById } from "./repo/catalog";

export type StockVariantRow = NonNullable<
  Awaited<ReturnType<typeof getVariantById>>
>;

export type StockContext = {
  requestVariant: StockVariantRow;
  poolVariant: StockVariantRow;
  saleVariant: StockVariantRow | null;
  unitsPerSale: number;
  isLinkedSale: boolean;
};

export function parseUnitsPerSale(unitsPerSale: string | number): number {
  const n =
    typeof unitsPerSale === "number" ? unitsPerSale : Number(unitsPerSale);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

export function isSaleVariant(variant: {
  inventoryVariantId: string | null;
}): boolean {
  return Boolean(variant.inventoryVariantId);
}

export function isInventoryPoolVariant(variant: {
  isInventoryOnly: boolean;
  inventoryVariantId: string | null;
}): boolean {
  return variant.isInventoryOnly && !variant.inventoryVariantId;
}

/** สต็อกขายได้ = floor(สต็อกคลัง / หน่วยต่อการขาย) */
export function computeSaleableStock(
  poolStockQty: number,
  unitsPerSale: number,
): number {
  if (unitsPerSale <= 0) return 0;
  return Math.floor(poolStockQty / unitsPerSale);
}

export function saleQtyToInventoryQty(
  saleQty: number,
  unitsPerSale: number,
): number {
  return saleQty * unitsPerSale;
}

export function inventoryQtyToSaleQty(
  inventoryQty: number,
  unitsPerSale: number,
): number {
  if (unitsPerSale <= 0) return 0;
  return Math.floor(inventoryQty / unitsPerSale);
}

export async function resolveStockContext(
  client: DbTransaction | DbClient,
  variantId: string,
): Promise<StockContext> {
  const variant = await getVariantById(client, variantId);
  if (!variant) throw new Error("Variant not found");

  if (variant.inventoryVariantId) {
    const pool = await getVariantById(client, variant.inventoryVariantId);
    if (!pool) throw new Error("Inventory pool variant not found");
    const unitsPerSale = parseUnitsPerSale(variant.unitsPerSale);
    return {
      requestVariant: variant,
      poolVariant: pool,
      saleVariant: variant,
      unitsPerSale,
      isLinkedSale: true,
    };
  }

  return {
    requestVariant: variant,
    poolVariant: variant,
    saleVariant: null,
    unitsPerSale: 1,
    isLinkedSale: false,
  };
}

export async function resolvePoolVariantId(
  client: DbTransaction | DbClient,
  variantId: string,
): Promise<string> {
  const ctx = await resolveStockContext(client, variantId);
  return ctx.poolVariant.id;
}

export function enrichVariantStockFields<
  T extends {
    id: string;
    stockQuantity: number;
    inventoryVariantId: string | null;
    unitsPerSale: string;
    isInventoryOnly: boolean;
  },
>(
  variant: T,
  poolById: Map<string, { stockQuantity: number }>,
): T & { poolStockQuantity: number | null; saleableStockQuantity: number } {
  if (variant.inventoryVariantId) {
    const pool = poolById.get(variant.inventoryVariantId);
    const units = parseUnitsPerSale(variant.unitsPerSale);
    const poolStock = pool?.stockQuantity ?? 0;
    return {
      ...variant,
      poolStockQuantity: poolStock,
      saleableStockQuantity: computeSaleableStock(poolStock, units),
      stockQuantity: computeSaleableStock(poolStock, units),
    };
  }

  return {
    ...variant,
    poolStockQuantity: variant.isInventoryOnly ? variant.stockQuantity : null,
    saleableStockQuantity: variant.stockQuantity,
  };
}
